import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { GlobalMicroButton } from './GlobalMicroButton';
import styles from './FilterBar.module.css';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export interface FilterBarProps {
  filters: {
    id: string;
    label: string;
    options: FilterOption[];
    value: string;
    onChange: (value: string) => void;
  }[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  showInviteButton?: boolean;
  onAddUser?: () => void;
  addButtonText?: string;
  showAddCompanyButton?: boolean;
  onAddCompany?: () => void;
  addCompanyButtonText?: string;
}

export function FilterBar({ 
  filters, 
  searchValue, 
  onSearchChange, 
  searchPlaceholder = "Search...",
  showInviteButton = false,
  onAddUser,
  addButtonText,
  showAddCompanyButton = false,
  onAddCompany,
  addCompanyButtonText
}: FilterBarProps) {
  const { theme } = useTheme();
  const { t } = useLocale();
  const isDarkMode = theme === 'dark';
  
  const handleInviteNewUser = () => {
    if (onAddUser) {
      onAddUser();
    }
  };

  const handleAddCompany = () => {
    if (onAddCompany) {
      onAddCompany();
    }
  };

  return (
    <div className={styles.filterBar}>
      {/* Search Input */}
      <div className={styles.searchContainer}>
        <img 
          src={isDarkMode ? "/assets/icons/magnifyingglass.svg" : "/assets/icons/magnifyingglass.black.svg"} 
          alt="Search" 
          width={12} 
          height={12} 
          className={styles.searchIcon}
        />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className={styles.searchInput}
        />
      </div>

      {/* Filter Bubbles */}
      {filters.length > 0 && (
        <div className={styles.filtersContainer}>
          {filters.map((filter) => (
            <div key={filter.id} className={styles.filterBubble}>
              <select
                value={filter.value}
                onChange={(e) => filter.onChange(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">{filter.label}</option>
                {filter.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} {option.count ? `(${option.count})` : ''}
                  </option>
                ))}
              </select>
              <img 
                src={isDarkMode ? "/assets/icons/chevron.down.svg" : "/assets/icons/chevron.down.black.svg"} 
                alt={t('project_tab_dropdown') || 'Dropdown'} 
                width={12} 
                height={12} 
                className={styles.chevronIcon}
              />
            </div>
          ))}
        </div>
      )}

      {/* Invite New User Button */}
      {showInviteButton && (
        <div className={styles.actionContainer}>
          <GlobalMicroButton
            onClick={handleInviteNewUser}
            icon={
              <img 
                src={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"} 
                alt={t('project_tab_add') || 'Add'} 
                width={12} 
                height={12} 
              />
            }
          >
            {addButtonText || t('home_invite_new_user')}
          </GlobalMicroButton>
        </div>
      )}

      {/* Add Company Button */}
      {showAddCompanyButton && (
        <div className={styles.actionContainer}>
          <GlobalMicroButton
            onClick={handleAddCompany}
            icon={
              <img 
                src={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"} 
                alt={t('project_tab_add') || 'Add'} 
                width={12} 
                height={12} 
              />
            }
          >
            {addCompanyButtonText || t('home_add_company_action')}
          </GlobalMicroButton>
        </div>
      )}

    </div>
  );
}
